import { usePersisted } from "./persisted";

export type SortKey = "due" | "recent" | "az" | "za";

/** Three-way rather than two toggles: "only never spoken" and "hide never
 *  spoken" are mutually exclusive, and two booleans would let you switch both
 *  on and see nothing at all. */
export type SpokenFilter = "all" | "only-never" | "hide-never";

/** Up Next's filter and sort state.
 *
 *  Persisted so the bishopric doesn't have to re-apply the same filters every
 *  time they open the app mid-meeting. The risk is a filter left on last week
 *  quietly hiding someone today, so anything non-default is counted on the
 *  Filters button and clearable in one tap.
 *
 *  The search box is deliberately NOT here — a stale search on open reads as
 *  the app being broken.
 */
export interface Filters {
  adults: boolean;
  youth: boolean;
  /** Hide anyone who spoke within settings.recentMonths. */
  hideRecent: boolean;
  spoken: SpokenFilter;
  sort: SortKey;
}

export const DEFAULT_FILTERS: Filters = {
  adults: true,
  youth: true,
  hideRecent: false,
  spoken: "all",
  sort: "due",
};

export const SORTS: Array<{ v: SortKey; label: string; short: string }> = [
  { v: "due", label: "Longest since speaking", short: "Longest first" },
  { v: "recent", label: "Most recently spoken", short: "Recent first" },
  { v: "az", label: "Name A–Z", short: "A–Z" },
  { v: "za", label: "Name Z–A", short: "Z–A" },
];

/** How many filters are hiding people right now. Sort is excluded — it
 *  reorders but never hides, so it can't cause the "where did they go?"
 *  confusion the badge exists to prevent. */
export function activeFilterCount(f: Filters): number {
  return (
    (f.adults === DEFAULT_FILTERS.adults ? 0 : 1) +
    (f.youth === DEFAULT_FILTERS.youth ? 0 : 1) +
    (f.hideRecent === DEFAULT_FILTERS.hideRecent ? 0 : 1) +
    (f.spoken === DEFAULT_FILTERS.spoken ? 0 : 1)
  );
}

export function useFilters() {
  const { value, set, reset } = usePersisted("sacrament.filters.v1", DEFAULT_FILTERS);
  return { filters: value, set, reset };
}
